'use client';

import { cn } from '@core/utils';
import { AnimatePresence, motion } from 'framer-motion';

import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { DONATION_ROUTES, ROUTES, WEL_MARKET_ROUTES } from '@/constants';

export type ServiceKey = 'wello' | 'donation' | 'market';

interface ServiceItem {
  key: ServiceKey;
  label: string;
  href: string;
  iconSrc: string;
}

const SERVICE_ITEMS: Record<ServiceKey, ServiceItem> = {
  wello: {
    key: 'wello',
    label: '웰로',
    href: ROUTES.HOME.pathname,
    iconSrc: '/assets/svgs/brand/wello-badge.png',
  },
  donation: {
    key: 'donation',
    label: '고향사랑기부',
    href: DONATION_ROUTES.HOME.pathname,
    iconSrc: '/assets/svgs/brand/donation-badge.png',
  },
  market: {
    key: 'market',
    label: '웰마켙',
    href: WEL_MARKET_ROUTES.HOME.pathname,
    iconSrc: '/assets/svgs/brand/market-badge.png',
  },
};

interface ServiceSwitchDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  currentService: ServiceKey;
  className?: string;
}

export const ServiceSwitchDropdown = ({
  isOpen,
  onClose,
  currentService,
  className,
}: ServiceSwitchDropdownProps) => {
  const items = (Object.keys(SERVICE_ITEMS) as ServiceKey[])
    .filter((key) => key !== currentService)
    .map((key) => SERVICE_ITEMS[key]);

  return (
    <AnimatePresence>
      {isOpen ? (
        <>
          <motion.div
            aria-hidden
            className="fixed inset-0 z-40 bg-Gray-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
          />
          <motion.div
            className={cn(
              'shadow-large absolute bottom-full left-0 z-50 mb-12 min-w-160 rounded-large bg-Gray-white px-16 py-12',
              className,
            )}
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 500, damping: 35 }}
            role="menu"
            aria-label="다른 서비스로 이동"
          >
            <ul className="flex flex-col gap-4">
              {items.map(({ key, label, href, iconSrc }) => (
                <li key={key}>
                  <CustomLink
                    href={href}
                    onClick={onClose}
                    className="flex items-center gap-8 rounded-medium py-8 duration-150"
                    role="menuitem"
                  >
                    <Image
                      alt=""
                      aria-hidden
                      className="size-32 shrink-0 rounded-medium"
                      src={iconSrc}
                      width={32}
                      height={32}
                    />
                    <span className="font-500 text-Gray-900 typo-body4">
                      {label}
                    </span>
                  </CustomLink>
                </li>
              ))}
            </ul>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
};
